import Header from "../../../../components/ui/Header";
import FooterSection from "../../../../components/ui/Footer";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../api/axiosInstance";
import { motion } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import ResumeScoreCard from "./components/ResumeScoreCard";
import PriorityFixes from "./components/PriorityFixes";
import ResumeImprovements from "./components/ResumeImprovements";
import NextBestAction from "./components/NextBestAction";
import ScoreBreakdown from "./components/ScoreBreakdown";
import AnalysisInsight from "./components/AnalysisInsight";
import KeywordAnalysis from "./components/KeywordAnalysis";

export default function ResumeAnalysis() {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loadingFix, setLoadingFix] = useState(false);
  const [resumeText, setResumeText] = useState("");
  const [highlight, setHighlight] = useState("");

  // ✅ ONLY useEffect — reads from localStorage saved by ResumeUpload
  useEffect(() => {
    try {
      const stored = localStorage.getItem("resume_analysis_data");
      const resume = localStorage.getItem("resume_original_text");
      
      if (resume) setResumeText(resume);
      if (!stored) return;

      const parsed = JSON.parse(stored);

      setAnalysis({
        score:           parsed.overallScore   || 0,
        jobMatchScore:   parsed.jobMatchScore  || 0,
        atsParsing:      parsed.atsParsing     || "FAIL",
        jobMatch:        parsed.jobMatch       || "LOW",
        recruiterImpact: parsed.recruiterImpact|| "LOW",
        keywords:        parsed.keywords       || { matchPercent: 0, matched: [], missing: [] },
        parsedData:      parsed.parsedData     || {},
        matchedSkills:   parsed.matchedSkills  || [],
        missingSkills:   parsed.missingSkills  || [],
        strengths:       parsed.strengths      || [],
        quickWins:       parsed.quickWins      || [],
        summaryVerdict:  parsed.summaryVerdict || "",
        fullReport:      parsed.fullReport     || {},
        role:            parsed.role           || "",
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleInlineFix = async (text) => {
    try {
      setLoadingFix(true);
      toast.loading("Improving with AI...");
      const res = await api.post("/api/resume/ai/fix", { content: text });
      const improved = res.data?.data;
      if (!improved) { toast.dismiss(); toast.error("Invalid AI response"); return; }
      const updated = resumeText.replace(text, improved);
      setResumeText(updated);
      localStorage.setItem("resume_original_text", updated);
      setHighlight(improved);
      toast.dismiss();
      toast.success("Improved successfully 🚀");
    } catch (err) {
      console.error(err);
      toast.dismiss();
      toast.error("AI improvement failed");
    } finally {
      setLoadingFix(false);
    }
  };

  const handleFullFix = async () => {
    try {
      const job = localStorage.getItem("job_description") || "";
      const resume = localStorage.getItem("resume_original_text") || "";
      const blob = new Blob([resume], { type: "text/plain" });
      const file = new File([blob], "resume.txt");
      const form = new FormData();
      form.append("resume", file);
      form.append("jobDescription", job);
      const res = await api.post("/api/resume/rewrite", form);
      const rewritten = res.data.rewritten?.join("\n") || "";
      localStorage.setItem("resume_fixed_version", rewritten);
      navigate("/candidate/resume-fixed");
    } catch (err) {
      console.error(err);
      toast.error("Rewrite failed");
    }
  };

  if (!analysis) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="pt-24 text-center text-gray-500">
          No resume analysis found.
        </main>
        <FooterSection />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      <Header />
      <main className="pt-24 pb-24">
        <div className="max-w-7xl mx-auto px-6">

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <ResumeScoreCard
              score={analysis.score}
              atsParsing={analysis.atsParsing}
              jobMatch={analysis.jobMatch}
              recruiterImpact={analysis.recruiterImpact}
            />
            <div className="mt-4">
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${analysis.score}%` }} />
              </div>
            </div>
          </motion.div>

          {/* ✅ AI EDITOR BUTTON */}
          <button
            onClick={() => {
              navigate("/candidate/resume/editor", {
                state: {
                  analysisResult: analysis,
                  parsedData:     analysis.parsedData,
                  jobRole:        localStorage.getItem("job_role") || "",
                  jobDescription: localStorage.getItem("job_description") || "",
                }
              });
            }}
            className="whitespace-nowrap bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-lg shadow hover:scale-105 transition"
          >
            ✨ Improve with AI Editor
          </button>
          <p className="text-xs text-gray-500 mt-1">Fix all issues instantly with AI ✨</p>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <AnalysisInsight score={analysis.score} />
              <ScoreBreakdown
                atsParsing={analysis.atsParsing}
                jobMatch={analysis.jobMatch}
                recruiterImpact={analysis.recruiterImpact}
              />
              <KeywordAnalysis keywords={analysis.keywords} />
              <PriorityFixes data={analysis} onFixClick={handleFullFix} />
              <ResumeImprovements data={analysis} />
            </div>

            <div className="space-y-6 sticky top-24 h-fit">
              <NextBestAction
                score={analysis.score}
                analysisResult={analysis}
                jobRole={localStorage.getItem("job_role") || ""}
                jobDescription={localStorage.getItem("job_description") || ""}
              />
              <div className="bg-white p-5 rounded-xl border text-sm text-gray-600">
                <p className="font-semibold text-gray-800 mb-2">Why this matters</p>
                <p>Recruiters spend ~6 seconds per resume. Fixing these issues can increase your chances by up to <b>2x</b>.</p>
                <p className="text-xs mt-2 text-gray-400">Based on 10,000+ resumes analyzed</p>
              </div>
            </div>
          </div>

        </div>
      </main>
      <FooterSection />
    </div>
  );
}